import * as React from 'react';
import { EditorContent, EditorContext, useEditor, type Editor } from '@tiptap/react';

// --- Tiptap Core Extensions ---
import { StarterKit } from '@tiptap/starter-kit';
import { Image } from '@tiptap/extension-image';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import { TextAlign } from '@tiptap/extension-text-align';
import { Typography } from '@tiptap/extension-typography';
import { Highlight } from '@tiptap/extension-highlight';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Selection } from '@tiptap/extensions';
import {
  Table,
  TableCell,
  TableHeader,
  TableRow,
} from '@/editor/tiptap/vendor/extension-table';

// --- UI Primitives ---
import { Button } from '@/editor/tiptap/components/tiptap-ui-primitive/button';
import { Spacer } from '@/editor/tiptap/components/tiptap-ui-primitive/spacer';
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from '@/editor/tiptap/components/tiptap-ui-primitive/toolbar';

// --- Tiptap Node ---
import { ImageUploadNode } from '@/editor/tiptap/components/tiptap-node/image-upload-node/image-upload-node-extension';
import { HorizontalRule } from '@/editor/tiptap/components/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension';
import '@/editor/tiptap/components/tiptap-node/blockquote-node/blockquote-node.scss';
import '@/editor/tiptap/components/tiptap-node/code-block-node/code-block-node.scss';
import '@/editor/tiptap/components/tiptap-node/horizontal-rule-node/horizontal-rule-node.scss';
import '@/editor/tiptap/components/tiptap-node/list-node/list-node.scss';
import '@/editor/tiptap/components/tiptap-node/image-node/image-node.scss';
import '@/editor/tiptap/components/tiptap-node/heading-node/heading-node.scss';
import '@/editor/tiptap/components/tiptap-node/paragraph-node/paragraph-node.scss';

// --- Tiptap UI ---
import { HeadingDropdownMenu } from '@/editor/tiptap/components/tiptap-ui/heading-dropdown-menu';
// import { ImageUploadButton } from "@/editor/tiptap/components/tiptap-ui/image-upload-button"
import { ListDropdownMenu } from '@/editor/tiptap/components/tiptap-ui/list-dropdown-menu';
import { BlockquoteButton } from '@/editor/tiptap/components/tiptap-ui/blockquote-button';
import { CodeBlockButton } from '@/editor/tiptap/components/tiptap-ui/code-block-button';
import {
  // ColorHighlightPopover,
  ColorHighlightPopoverContent,
  // ColorHighlightPopoverButton,
} from '@/editor/tiptap/components/tiptap-ui/color-highlight-popover';
import {
  // LinkPopover,
  LinkContent,
  // LinkButton,
} from '@/editor/tiptap/components/tiptap-ui/link-popover';
import { MarkButton } from '@/editor/tiptap/components/tiptap-ui/mark-button';
import { TextAlignButton } from '@/editor/tiptap/components/tiptap-ui/text-align-button';
// import { UndoRedoButton } from '@/editor/tiptap/components/tiptap-ui/undo-redo-button';

// --- Icons ---
import { ArrowLeftIcon } from '@/editor/tiptap/components/tiptap-icons/arrow-left-icon';
import { HighlighterIcon } from '@/editor/tiptap/components/tiptap-icons/highlighter-icon';
import { LinkIcon } from '@/editor/tiptap/components/tiptap-icons/link-icon';

// --- Hooks ---
import { useIsMobile } from '@/editor/tiptap/hooks/use-mobile';
import { useWindowSize } from '@/editor/tiptap/hooks/use-window-size';
import { useCursorVisibility } from '@/editor/tiptap/hooks/use-cursor-visibility';

// --- Components ---
// import { ThemeToggle } from "@/editor/tiptap/components/tiptap-templates/simple/theme-toggle"

// --- Lib ---
import { handleImageUpload, MAX_FILE_SIZE } from '@/editor/tiptap/lib/tiptap-utils';

// --- Styles ---
import '@/editor/tiptap/components/tiptap-templates/simple/simple-editor.scss';

const MainToolbarContent = ({
  // onHighlighterClick,
  // onLinkClick,
  isMobile,
  editor,
  enableTables,
}: {
  onHighlighterClick: () => void;
  onLinkClick: () => void;
  isMobile: boolean;
  editor: Editor | null;
  enableTables: boolean;
}) => {
  return (
    <>
      <Spacer />
      {/* 
      <ToolbarGroup>
        <UndoRedoButton action="undo" />
        <UndoRedoButton action="redo" />
      </ToolbarGroup> */}

      {/* <ToolbarSeparator /> */}

      <ToolbarGroup>
        <HeadingDropdownMenu levels={[1, 2, 3, 4]} portal={isMobile} />
        <ListDropdownMenu
          types={['bulletList', 'orderedList', 'taskList']}
          portal={isMobile}
        />
        <BlockquoteButton />
        <CodeBlockButton />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="bold" />
        <MarkButton type="italic" />
        <MarkButton type="strike" />
        <MarkButton type="code" />
        <MarkButton type="underline" />
        {/* {!isMobile ? (
          <ColorHighlightPopover />
        ) : (
          <ColorHighlightPopoverButton onClick={onHighlighterClick} />
        )}
        {!isMobile ? <LinkPopover /> : <LinkButton onClick={onLinkClick} />} */}
      </ToolbarGroup>

      {/* <ToolbarSeparator /> */}
      {/* 
      <ToolbarGroup>
        <MarkButton type="superscript" />
        <MarkButton type="subscript" />
      </ToolbarGroup> */}

      <ToolbarSeparator />

      <ToolbarGroup>
        <TextAlignButton align="left" />
        <TextAlignButton align="center" />
        <TextAlignButton align="right" />
        <TextAlignButton align="justify" />
      </ToolbarGroup>

      {enableTables && (
        <>
          <ToolbarSeparator />
          <ToolbarGroup>
            <Button
              type="button"
              data-style="ghost"
              onClick={() =>
                (editor as any)
                  ?.chain()
                  .focus()
                  .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                  .run()
              }
              disabled={
                !((editor as any)?.can?.().insertTable?.({ rows: 3, cols: 3 }))
              }
            >
              Insert Table
            </Button>
            <Button
              type="button"
              data-style="ghost"
              onClick={() => (editor as any)?.chain().focus().addRowAfter().run()}
              disabled={!((editor as any)?.can?.().addRowAfter?.())}
            >
              Row +
            </Button>
            <Button
              type="button"
              data-style="ghost"
              onClick={() => (editor as any)?.chain().focus().deleteRow().run()}
              disabled={!((editor as any)?.can?.().deleteRow?.())}
            >
              Row -
            </Button>
            <Button
              type="button"
              data-style="ghost"
              onClick={() => (editor as any)?.chain().focus().addColumnAfter().run()}
              disabled={!((editor as any)?.can?.().addColumnAfter?.())}
            >
              Col +
            </Button>
            <Button
              type="button"
              data-style="ghost"
              onClick={() => (editor as any)?.chain().focus().deleteColumn().run()}
              disabled={!((editor as any)?.can?.().deleteColumn?.())}
            >
              Col -
            </Button>
            <Button
              type="button"
              data-style="ghost"
              onClick={() => (editor as any)?.chain().focus().deleteTable().run()}
              disabled={!((editor as any)?.can?.().deleteTable?.())}
            >
              Delete Table
            </Button>
          </ToolbarGroup>
        </>
      )}

      {/* <ToolbarSeparator /> */}

      {/* <ToolbarGroup>
        <ImageUploadButton text="Add" />
      </ToolbarGroup> */}

      <Spacer />

      {/* {isMobile && <ToolbarSeparator />} */}

      {/* <ToolbarGroup>
        <ThemeToggle />
      </ToolbarGroup> */}
    </>
  );
};

const MobileToolbarContent = ({
  type,
  onBack,
}: {
  type: 'highlighter' | 'link';
  onBack: () => void;
}) => (
  <>
    <ToolbarGroup>
      <Button data-style="ghost" onClick={onBack}>
        <ArrowLeftIcon className="tiptap-button-icon" />
        {type === 'highlighter' ? (
          <HighlighterIcon className="tiptap-button-icon" />
        ) : (
          <LinkIcon className="tiptap-button-icon" />
        )}
      </Button>
    </ToolbarGroup>

    <ToolbarSeparator />

    {type === 'highlighter' ? (
      <ColorHighlightPopoverContent />
    ) : (
      <LinkContent />
    )}
  </>
);

interface SimpleEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  enableTables?: boolean;
}

export function SimpleEditor({
  value,
  onChange,
  placeholder,
  enableTables = false,
}: SimpleEditorProps) {
  const isMobile = useIsMobile();
  const { height } = useWindowSize();
  const [mobileView, setMobileView] = React.useState<
    'main' | 'highlighter' | 'link'
  >('main');
  const toolbarRef = React.useRef<HTMLDivElement>(null);

  const extensions = React.useMemo(() => {
    const list = [
      StarterKit.configure({
        horizontalRule: false,
        link: {
          openOnClick: false,
          enableClickSelection: true,
        },
      }),
      HorizontalRule,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Highlight.configure({ multicolor: true }),
      Image,
      Typography,
      Superscript,
      Subscript,
      Selection,
      Placeholder.configure({
        placeholder: placeholder,
      }),
      ImageUploadNode.configure({
        accept: 'image/*',
        maxSize: MAX_FILE_SIZE,
        limit: 3,
        upload: handleImageUpload,
        onError: (error) => console.error('Upload failed:', error),
      }),
    ];

    if (enableTables) {
      list.push(
        Table.configure({
          resizable: true,
          allowTableNodeSelection: true,
        }),
        TableRow,
        TableHeader,
        TableCell,
      );
    }

    return list;
  }, [enableTables, placeholder]);

  const editor = useEditor({
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
    editorProps: {
      attributes: {
        autocomplete: 'off',
        autocorrect: 'off',
        autocapitalize: 'off',
        'aria-label': 'Main content area, start typing to enter text.',
        class: 'simple-editor',
      },
    },
    extensions,
  });

  const rect = useCursorVisibility({
    editor,
    overlayHeight: toolbarRef.current?.getBoundingClientRect().height ?? 0,
  });

  React.useEffect(() => {
    if (!isMobile && mobileView !== 'main') {
      setMobileView('main');
    }
  }, [isMobile, mobileView]);

  return (
    <div className="simple-editor-wrapper">
      <EditorContext.Provider value={{ editor }}>
        <Toolbar
          ref={toolbarRef}
          style={{
            ...(isMobile
              ? {
                bottom: `calc(100% - ${height - rect.y}px)`,
              }
              : {}),
          }}
        >
          {mobileView === 'main' ? (
            <MainToolbarContent
              onHighlighterClick={() => setMobileView('highlighter')}
              onLinkClick={() => setMobileView('link')}
              isMobile={isMobile}
              editor={editor}
              enableTables={enableTables}
            />
          ) : (
            <MobileToolbarContent
              type={mobileView === 'highlighter' ? 'highlighter' : 'link'}
              onBack={() => setMobileView('main')}
            />
          )}
        </Toolbar>

        <EditorContent
          editor={editor}
          role="presentation"
          className="simple-editor-content"
        />
      </EditorContext.Provider>
    </div>
  );
}
